import { Icon, Tooltip } from '@efira/ui';
import type { IconName } from '@efira/ui';

import { useConsoleStream } from '../stream';
import type { StreamCounters } from '../stream';
import { LIVE_WORDS, useLiveKind } from '../liveState';

/**
 * Полоса счётчиков эфира: шесть чисел со значками и подсказками.
 *
 * ПОЯВЛЯЕТСЯ ТОЛЬКО ПРИ АКТИВНОЙ СЕССИИ - как у эталона. Шесть нулей без
 * эфира ничего не сообщают и занимают место, которое нужно ленте.
 *
 * ЧИСЛА СЧИТАЮТСЯ ИЗ ТОГО ЖЕ ПОТОКА, что наполняет ленту (`stream.tsx`).
 * Отдельный пакет со счётчиками был бы вторым источником правды о том же
 * самом: «в счётчике 12 комментариев, в ленте 11» - и поди разберись, кто
 * прав.
 *
 * У КАЖДОГО СЧЁТЧИКА ПОДСКАЗКА СЛОВАМИ. Значок плюс число - это ребус;
 * «монеты» и «подарки» в TikTok различаются, и стример вправе знать, что
 * именно он видит.
 *
 * НАЗВАНИЕ - В ПОДСКАЗКЕ, А НЕ В СТРОКЕ. Решение владельца 2026-09-29 (4),
 * находка 32 аудита. Единица 18 показывала слово при ширине полосы от
 * 620 px - а правая колонка такой ширины не бывает на заводской геометрии,
 * и порог был мёртвым: слов не видел никто, а код обещал их. Теперь всегда
 * значок и число; название - первой строкой подсказки и для чтеца
 * (`sr-only`), который подсказку при наведении не получает.
 */
const ITEMS: {
  id: keyof StreamCounters;
  icon: IconName;
  label: string;
  hint: string;
}[] = [
  {
    id: 'viewers',
    icon: 'eye',
    label: 'Зрители',
    hint: 'Сколько разных зрителей проявились {span}: написали, лайкнули, вошли или отправили подарок',
  },
  { id: 'comments', icon: 'chat', label: 'Комментарии', hint: 'Сообщений в чате {span}' },
  { id: 'likes', icon: 'heart', label: 'Лайки', hint: 'Лайков {span}, с учётом серий' },
  { id: 'gifts', icon: 'gift', label: 'Подарки', hint: 'Штук подарков {span}, с учётом серий' },
  { id: 'coins', icon: 'coins', label: 'Монеты', hint: 'Монет в подарках {span}' },
  {
    id: 'followers',
    icon: 'personAdd',
    label: 'Новые подписчики',
    hint: 'Подписались {span}',
  },
];

export function StatStrip() {
  const stream = useConsoleStream();
  /* Слово периода - из одного источника: во время симулятора счётчики
     считают его события, и «за эфир» было бы неправдой (единица 22). */
  const kind = useLiveKind();
  const span = LIVE_WORDS[kind].span;

  return (
    <div
      data-right="stat-strip"
      data-live-place="stat-strip"
      data-live-state={kind}
      data-control-row="stat-strip"
      className="@container border-foreground/10 flex min-h-11 items-center justify-between gap-1 overflow-hidden border-y px-3 py-2"
    >
      {ITEMS.map((item, index) => (
        <Tooltip
          key={item.id}
          text={`${item.label}. ${item.hint.replace('{span}', span)}`}
          side="bottom"
          wide
        >
          {/* Разделители между ячейками, как у эталона: шесть чисел подряд
              без границ читаются как одно длинное. */}
          <span
            data-right-stat={item.id}
            className={`text-foreground/80 flex items-center gap-1 text-xs ${
              index === 0 ? '' : 'border-foreground/10 border-s ps-1'
            }`}
          >
            <Icon name={item.icon} size="sm" />
            <span className="text-foreground tabular-nums">{stream.counters[item.id]}</span>
            <span className="sr-only">{item.label}</span>
          </span>
        </Tooltip>
      ))}
    </div>
  );
}
